/**
 * Client-side logging utility
 * Provides structured logging with different severity levels
 */

export const LogLevel = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
} as const;

export type LogLevel = typeof LogLevel[keyof typeof LogLevel];

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: Error;
  userId?: string;
  sessionId?: string;
}

class Logger {
  private isDevelopment: boolean;
  private sessionId: string;
  private logBuffer: LogEntry[] = [];
  private readonly MAX_BUFFER_SIZE = 100;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.sessionId = this.generateSessionId();
    this.setupGlobalErrorHandlers();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private setupGlobalErrorHandlers(): void {
    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.error('Uncaught error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled promise rejection', {
        reason: event.reason,
        promise: event.promise,
      });
    });
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    contextOrError?: Record<string, unknown> | Error,
    error?: Error
  ): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
    };

    // Handle context and error parameters
    if (contextOrError instanceof Error) {
      entry.error = contextOrError;
    } else if (contextOrError) {
      entry.context = contextOrError;
    }

    if (error) {
      entry.error = error;
    }

    return entry;
  }

  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry);

    // Keep buffer size under control
    if (this.logBuffer.length > this.MAX_BUFFER_SIZE) {
      this.logBuffer.shift();
    }
  }

  private formatConsoleOutput(entry: LogEntry): void {
    const emoji = {
      [LogLevel.DEBUG]: '🔍',
      [LogLevel.INFO]: 'ℹ️',
      [LogLevel.WARN]: '⚠️',
      [LogLevel.ERROR]: '❌',
    };

    const styles = {
      [LogLevel.DEBUG]: 'color: #888',
      [LogLevel.INFO]: 'color: #2196F3',
      [LogLevel.WARN]: 'color: #FF9800',
      [LogLevel.ERROR]: 'color: #F44336; font-weight: bold',
    };

    const prefix = `${emoji[entry.level]} [${entry.level}] ${entry.timestamp}`;

    console.group(`%c${prefix}`, styles[entry.level]);
    console.log(entry.message);

    if (entry.context) {
      console.log('Context:', entry.context);
    }

    if (entry.error) {
      console.error('Error:', entry.error);
      if (entry.error.stack) {
        console.log('Stack:', entry.error.stack);
      }
    }

    console.groupEnd();
  }

  private shouldLog(level: LogLevel): boolean {
    // In production, only log WARN and ERROR
    if (!this.isDevelopment) {
      return level === LogLevel.WARN || level === LogLevel.ERROR;
    }
    return true;
  }

  debug(message: string, context?: Record<string, unknown>): void {
    const entry = this.createLogEntry(LogLevel.DEBUG, message, context);
    this.addToBuffer(entry);

    if (this.shouldLog(LogLevel.DEBUG)) {
      this.formatConsoleOutput(entry);
    }
  }

  info(message: string, context?: Record<string, unknown>): void {
    const entry = this.createLogEntry(LogLevel.INFO, message, context);
    this.addToBuffer(entry);

    if (this.shouldLog(LogLevel.INFO)) {
      this.formatConsoleOutput(entry);
    }
  }

  warn(message: string, context?: Record<string, unknown>): void {
    const entry = this.createLogEntry(LogLevel.WARN, message, context);
    this.addToBuffer(entry);

    if (this.shouldLog(LogLevel.WARN)) {
      this.formatConsoleOutput(entry);
    }

    // TODO: Send to remote logging service in production
  }

  error(message: string, errorOrContext?: Error | Record<string, unknown>, error?: Error): void {
    const entry = this.createLogEntry(LogLevel.ERROR, message, errorOrContext, error);
    this.addToBuffer(entry);

    if (this.shouldLog(LogLevel.ERROR)) {
      this.formatConsoleOutput(entry);
    }

    // TODO: Send to remote logging service in production
    // This could be Sentry, LogRocket, Firebase Crashlytics, etc.
  }

  /**
   * Get recent logs (useful for debugging or sending to support)
   */
  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logBuffer.slice(-count);
  }

  /**
   * Clear the log buffer
   */
  clearLogs(): void {
    this.logBuffer = [];
  }

  /**
   * Export logs as JSON (useful for bug reports)
   */
  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }

  /**
   * Set user context for all subsequent logs
   */
  setUserContext(userId: string): void {
    this.logBuffer.forEach(entry => {
      entry.userId = userId;
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Convenience exports
export const debug = logger.debug.bind(logger);
export const info = logger.info.bind(logger);
export const warn = logger.warn.bind(logger);
export const error = logger.error.bind(logger);
